<template>
  <Layout>
    <section class="marketplace--subscriptions">
      <header>
        <h2 class="marketplace-heading">{{ 'Subscriptions' | t }}</h2>
      </header>
      <main>
        <EmptyCartTeaser v-if="!loading && !isSearchingOrFiltering && !subscriptions.length">
          <template v-slot:header>
            <h2 class="text-xl fw-strong">{{ 'You have no subscriptions' | t }}</h2>
            <p class="m-0">{{ 'Shop the following categories for Sloan parts' | t }}</p>
          </template>
        </EmptyCartTeaser>
        <div v-else>
          <SloLoader :loading="loading" size="xl">
            <SloTabs
              items="start"
              default-value="active"
              :value="queryParams.status"
              :is-filter-tabs="true"
              @input="handleStatusChange"
            >
              <SloTab name="active" :title="'Active' | t" />
              <SloTab name="cancelled" :title="'Cancelled' | t" />
            </SloTabs>
            <SloLoader :loading="searchAndFilterLoading" size="xl">
              <div
                v-if="!hasSearchAndFilterResults && isSearchingOrFiltering"
                class="marketplace--subscriptions-no-results"
              >
                <p>{{ 'No subscriptions found' | t }}</p>
              </div>
              <div
                v-for="(subscription, index) in subscriptions"
                :key="`subscription-${index}`"
                class="mt-lg"
              >
                <MarketplaceTable :id="`marketplace-subscription-${subscription?.number}`">
                  <template v-slot:header>
                    <MarketplaceTableHeader>
                      <template v-slot:titles>
                        <div class="flex items-start justify-between md:flex-col md:justify-start">
                          <h4 class="text-base fw-strong">{{ 'Subscription Number' | t }}</h4>
                          <span class="text-base c-gray-700">{{ subscription?.number }}</span>
                        </div>
                        <div class="flex items-start justify-between md:flex-col md:justify-start">
                          <h4 class="text-base fw-strong">
                            {{ subscription?.isCancelled ? 'Subscription Cancelled' : 'Subscription Started' | t }}
                          </h4>
                          <span class="text-base c-gray-700">
                            {{ subscription?.isCancelled ? subscription?.cancelledDate : subscription?.startDate }}
                          </span>
                        </div>
                      </template>
                      <template v-slot:buttons>
                        <SubscriptionCancelButton
                          v-if="!subscription?.isCancelled"
                          :disabled="subscription?.isSubscriptionDisabled"
                          :subscription-id="subscription?.number"
                        />
                        <SloButton
                          v-if="!subscription?.isCancelled"
                          :to="{
                              name: 'marketplace-account-subscription',
                              params: {subscriptionId: subscription?.number},
                              query: currentRouteFullPath ? {back: currentRouteFullPath} : {}
                            }"
                          fill="outline"
                        >
                          {{ 'View Subscription' | t }}
                        </SloButton>
                      </template>
                    </MarketplaceTableHeader>
                  </template>
                  <template v-slot:body>
                    <MarketplaceRow
                      :key="`subscription-entry-${index}`"
                      :title="subscription?.product?.code"
                      :description="subscription?.product?.title"
                      :sku="subscription?.product?.code"
                      :product-type="subscription?.product?.type"
                      :price="subscription?.product?.subscriptionPrice?.formattedValue"
                      :uom="subscription?.product?.uom"
                      :quantity="subscription?.quantity"
                      :total="subscription?.total?.formattedValue"
                      :subscription-id="subscription?.number"
                      :subscription-frequency="subscription?.product?.subscriptionFrequency"
                      :subscription-percentage-discount="subscription?.product?.subscriptionPercentageDiscount"
                    >
                      <template v-slot:image>
                        <SloProductImage :source="subscription?.product?.images | getProductThumbnail" />
                      </template>
                      <template v-if="!subscription?.isCancelled" v-slot:subscription-update>
                        <SubscriptionUpdateButton
                          :disabled="subscription?.isSubscriptionDisabled"
                          :subscription-id="subscription?.number"
                          :subscription-frequencies="subscription?.product?.subscriptionFrequencies"
                          :current-quantity="subscription?.quantity"
                          :current-subscription-frequency-name="subscription?.product?.subscriptionFrequency"
                          @subscriptionUpdated="getSubscriptions"
                        />
                      </template>
                      <template v-if="!subscription?.isCancelled" v-slot:subscription-next-shipment>
                        <div class="flex flex-col items-start gap-sm md:flex-row md:items-center">
                          <div class="flex items-center gap-sm">
                            <SloIcon name="Timer" class="c-green-400" />
                            <span class="text-base fw-strong">
                              {{ 'Next ship date:' | t }} {{ subscription?.nextShipDate }}
                            </span>
                          </div>
                          <SubscriptionSkipNextShipmentButton
                            :disabled="subscription?.isSubscriptionDisabled"
                            :subscription-id="subscription?.number"
                            @subscriptionUpdated="getSubscriptions"
                          />
                        </div>
                      </template>
                    </MarketplaceRow>
                  </template >
                </MarketplaceTable>
              </div>
            </SloLoader>
          </SloLoader>
        </div>
      </main>
    </section>
  </Layout>
</template>

<script>
import {debounce} from 'vue-debounce';
import t from '../../main/i18n';
import {getProductThumbnail, handleApiErrors} from '../../main/utils';
import {marketplaceApi} from '../apis/marketplace';
import Layout from '../components/Layout.vue';
import EmptyCartTeaser from '../components/EmptyCartTeaser.vue';
import SloLoader from '../../../lib/components/SloLoader.vue';
import SloTabs from '../../../lib/components/SloTabs.vue';
import SloTab from '../../../lib/components/SloTab.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloProductImage from '../../../lib/components/SloProductImage.vue';
import SloIcon from '../../../lib/components/SloIcon.vue';
import SloModal from '../../../lib/components/SloModal.vue';
import SloField from '../../../lib/components/SloField.vue';
import SloLabel from '../../../lib/components/SloLabel.vue';
import SloSelect from '../../../lib/components/SloSelect.vue';
import MarketplaceTable from '../components/MarketplaceTable.vue';
import MarketplaceRow from '../components/MarketplaceRow.vue';
import MarketplaceTableHeader from '../components/MarketplaceTableHeader.vue';
import MarketplaceTableFooter from '../components/MarketplaceTableFooter.vue';
import SubscriptionCancelButton from '../components/SubscriptionCancelButton.vue';
import SubscriptionSkipNextShipmentButton from '../components/SubscriptionSkipNextShipmentButton.vue';
import SubscriptionUpdateButton from '../components/SubscriptionUpdateButton.vue';

export default {
  components: {
    Layout,
    EmptyCartTeaser,
    SloLoader,
    SloTabs,
    SloTab,
    SloButton,
    SloProductImage,
    SloIcon,
    SloModal,
    SloField,
    SloLabel,
    SloSelect,
    MarketplaceTable,
    MarketplaceRow,
    MarketplaceTableHeader,
    MarketplaceTableFooter,
    SubscriptionCancelButton,
    SubscriptionUpdateButton,
    SubscriptionSkipNextShipmentButton
  },
  filters: {
    t,
    getProductThumbnail
  },
  data() {
    return {
      loading: true,
      searchAndFilterLoading: false,
      isFirstPageLoad: true,
      showSkipModal: false,
      showCancelModal: false,
      subscriptions: [],
      currentRouteFullPath: '',
      queryParams: {status: ''}
    };
  },
  computed: {
    isSearchingOrFiltering() {
      return this.queryParams.status;
    },
    hasSearchAndFilterResults() {
      return this.isSearchingOrFiltering && this.subscriptions.length;
    }
  },
  watch: {
    $route() {
      this.currentRouteFullPath = this.$router.currentRoute.fullPath;
    },
    queryParams: {
      handler() {
        this.searchAndFilterLoading = true;
        (debounce(() => this.updateQuery(), '300ms'))();
      },
      deep: true
    }
  },
  created() {
    this.queryParams = {...this.queryParams, ...this.$route.query};
    this.currentRouteFullPath = this.$router.currentRoute.fullPath;
  },
  methods: {
    async updateQuery() {
      const query = Object.entries(this.queryParams).reduce((acc, [key, val]) => {
        if (!val) return acc;
        return {...acc, [key]: val};
      }, {});

      await this.getSubscriptions();

      if (!this.isFirstPageLoad) {
        await this.$router.push({
          name: 'marketplace-account-subscriptions',
          query: {...query}
        }).catch(() => {});
      }
      this.isFirstPageLoad = false;
    },
    async getSubscriptions() {
      await marketplaceApi.getCurrentUserSubscriptions({
        status: this.queryParams.status ?? 'active'
      })
        .then(res => {
          this.loading = false;
          this.searchAndFilterLoading = false;
          this.subscriptions = res.data.subscriptionList;
        })
        .catch(e => {
          this.loading = false;
          this.searchAndFilterLoading = false;
          handleApiErrors(e, this, 'Error loading subscriptions');
        });
    },
    handleStatusChange(status) {
      this.queryParams.status = status;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../scss/variables';
@import '../../../scss/system';

.marketplace--subscriptions {
  &-no-results {
    > p {
      margin: 3.2rem 0;
      font-size: 2.4rem;
    }
  }

  :deep(.slo-tabs) {
    nav {
      overflow: scroll;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    nav > ul {
      padding: 0;
      border-bottom: 1px solid $color-gray-300;
    }

    > main {
      margin-block: 3.2rem;
      overflow: initial;
    }
  }

  :deep(.slo-tab) {
    text-transform: uppercase;
    font-weight: 500;
  }
}
</style>
